const LOG_LEVELS = {
  DEBUG: 1,
  INFO: 2,
  WARN: 3,
  ERROR: 4,
} as const;
type LOG_LEVEL = keyof typeof LOG_LEVELS;

// Helper to prepend context to log messages
function ctx(msg = "") {
  return `${Logger.context} ${msg}`;
}

class Logger {
  private level: (typeof LOG_LEVELS)[LOG_LEVEL];
  private callback:
    | ((level: LOG_LEVEL, msg: string, ...args: any[]) => void)
    | null = null;
  private shouldBind = import.meta.env.VITE_LOGGER_BIND_CONSOLE === "true";
  private static isWorker = !globalThis.window;
  static context = Logger.isWorker ? "sw " : "web";

  constructor(levelName: string = import.meta.env.VITE_LOG_LEVEL || "WARN") {
    this.level = LOG_LEVELS[levelName as LOG_LEVEL] || LOG_LEVELS.WARN;
    this.bindConsoleMethods();
    this.info(`[LOGGER] Logger initialized at ${this.levelAsString()}`);
  }

  // IMPORTANT NOTE:
  // We bind console methods directly to preserve call source in dev tools.
  // This shows the correct file/line instead of always showing "logger.ts:XX".
  // Only enabled if VITE_LOGGER_BIND_CONSOLE===true. setLevel (i.e. via url param) has no effect.
  private bindConsoleMethods() {
    if (!this.shouldBind) return;
    console.warn(
      ctx(
        "[LOGGER] CAUTION! Binding console methods to preserve call source! Will not reflect runtime level changes. Will not call logger callbacks. `VITE_LOGGER_BIND_CONSOLE=false` to disable"
      )
    );
    if (this.level <= LOG_LEVELS.DEBUG)
      this.debug = console.debug.bind(console, ctx());
    if (this.level <= LOG_LEVELS.INFO)
      this.info = console.info.bind(console, ctx());
    if (this.level <= LOG_LEVELS.WARN)
      this.warn = console.warn.bind(console, ctx());
    if (this.level <= LOG_LEVELS.ERROR)
      this.error = console.error.bind(console, ctx());
  }

  setLevel(levelName: string) {
    const levelCaps = levelName.toUpperCase();
    if (this.shouldBind) {
      this.warn(
        "[LOGGER] Note: Console binding active - level changes don't affect bound methods"
      );
    } else if (LOG_LEVELS.hasOwnProperty(levelCaps)) {
      this.level = LOG_LEVELS[levelCaps as LOG_LEVEL];
      console.info(ctx(`[LOGGER] Log level set to ${levelCaps}`));
    } else {
      console.warn(
        ctx(
          `[LOGGER] Invalid log level << ${levelName} >>. Keeping current log level (${this.levelAsString()}). Valid args: ${Object.keys(LOG_LEVELS).join(", ")}`
        )
      );
    }
    return this.levelAsString();
  }

  setCallback(cb: typeof this.callback) {
    this.callback = cb;
  }

  private levelAsString(): LOG_LEVEL {
    return Object.keys(LOG_LEVELS)[this.level - 1] as LOG_LEVEL;
  }

  private shouldLog(levelName: LOG_LEVEL) {
    return this.level <= LOG_LEVELS[levelName];
  }

  debug(msg: string, ...args: any[]) {
    if (!this.shouldLog("DEBUG")) return;
    console.debug(ctx(msg), ...args);
    this.callback?.("DEBUG", msg, ...args);
  }

  info(msg: string, ...args: any[]) {
    if (!this.shouldLog("INFO")) return;
    console.info(ctx(msg), ...args);
    this.callback?.("INFO", msg, ...args);
  }

  warn(msg: string, ...args: any[]) {
    if (!this.shouldLog("WARN")) return;
    console.warn(ctx(msg), ...args);
    this.callback?.("WARN", msg, ...args);
  }

  error(msg: string, ...args: any[]) {
    if (!this.shouldLog("ERROR")) return;
    console.error(ctx(msg), ...args);
    this.callback?.("ERROR", msg, ...args);
  }
}
const logger = new Logger();
export { logger, LOG_LEVELS };
export type { LOG_LEVEL };
